import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Eye, EyeOff, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PERMISSION_KEYS = ["inventory", "restock", "employees", "timeclock", "sales", "orders", "pos", "customers", "menu"];
const VERB_BY_KEY = {
  inventory: ["none", "view", "edit"],
  restock: ["none", "view", "create", "approve"],
  employees: ["none", "view", "manage"],
  timeclock: ["none", "own", "view", "edit"],
  sales: ["none", "view"],
  orders: ["none", "view", "manage"],
  pos: ["none", "view", "manage"],
  customers: ["none", "view", "manage"],
  menu: ["none", "view", "manage"],
};
const DEFAULT_PERMS_BY_ROLE = {
  super_admin: Object.fromEntries(PERMISSION_KEYS.map((k) => [k, "manage"])),
  admin: Object.fromEntries(PERMISSION_KEYS.map((k) => [k, "manage"])),
  manager: { inventory: "edit", restock: "approve", employees: "manage", timeclock: "edit", sales: "view", orders: "manage", pos: "manage", customers: "manage", menu: "manage" },
  chef: { inventory: "edit", restock: "create", employees: "none", timeclock: "own", sales: "none", orders: "view", pos: "none", customers: "none", menu: "view" },
  cashier: { inventory: "view", restock: "create", employees: "none", timeclock: "own", sales: "none", orders: "manage", pos: "manage", customers: "manage", menu: "view" },
  employee: { inventory: "view", restock: "create", employees: "none", timeclock: "own", sales: "none", orders: "view", pos: "view", customers: "view", menu: "view" },
};

const empty = {
  email: "", password: "", name: "", role: "employee", position: "", hourly_rate: 15,
  store_id: "", assigned_store_ids: [], permissions: DEFAULT_PERMS_BY_ROLE.employee,
  work_agreement: "", can_view_employees: false,
};

export default function Employees() {
  const { sid } = useParams();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isAdmin = isSuper || user?.role === "admin";

  const [list, setList] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    const url = sid ? `/employees?store_id=${sid}` : "/employees";
    return api.get(url).then((r) => setList(r.data));
  }, [sid]);
  useEffect(() => { load(); api.get("/stores").then((r) => setStores(r.data)); }, [load]);

  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";
  const storeNames = (ids) => (ids || []).map(storeName).filter(Boolean).join(", ") || "—";
  // Prefer the multi-store `assigned_store_ids`; fall back to the legacy single `store_id`.
  const employeeStoreIds = (e) =>
    e?.assigned_store_ids?.length ? e.assigned_store_ids : (e?.store_id ? [e.store_id] : []);

  const onRoleChange = (r) => setForm((f) => ({ ...f, role: r, permissions: DEFAULT_PERMS_BY_ROLE[r] || f.permissions }));

  const save = async () => {
    try {
      const shared = {
        name: form.name, position: form.position,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        store_id: form.store_id || null,
        assigned_store_ids: (form.assigned_store_ids || []).filter(Boolean),
        work_agreement: form.work_agreement,
        role: form.role,
        permissions: form.permissions,
        can_view_employees: form.role === "manager" ? form.can_view_employees : false,
      };
      if (editing) {
        await api.patch(`/employees/${editing.id}`, shared);
        // sync store assignments (best-effort)
        const currAssignments = await api.get(`/employees/${editing.id}/store-assignments`).then((r) => r.data);
        const target = new Set(shared.assigned_store_ids);
        const current = new Set(currAssignments.map((a) => a.store_id));
        for (const s of target) if (!current.has(s)) await api.post(`/employees/${editing.id}/store-assignments`, { user_id: editing.id, store_id: s });
        for (const s of current) if (!target.has(s)) await api.delete(`/employees/${editing.id}/store-assignments/${s}`);
        toast.success("Saved");
      } else {
        const { data } = await api.post("/employees", { email: form.email, password: form.password, ...shared });
        if (data?.welcome_email_sent) toast.success(`Welcome email sent to ${data.email}`);
        else if (data?.welcome_email_sent === false) toast.warning("Employee created, but welcome email could not be sent");
        else toast.success("Employee created");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      email: e.email, password: "", name: e.name, role: e.role, position: e.position || "",
      hourly_rate: e.hourly_rate || 0,
      store_id: e.store_id || "",
      assigned_store_ids: e.assigned_store_ids || (e.store_id ? [e.store_id] : []),
      permissions: e.permissions || DEFAULT_PERMS_BY_ROLE[e.role] || DEFAULT_PERMS_BY_ROLE.employee,
      work_agreement: e.work_agreement || "",
      can_view_employees: !!e.can_view_employees,
    });
    setOpen(true);
  };
  const openNew = () => {
    const initial = { ...empty };
    if (sid) initial.store_id = sid;
    if (sid) initial.assigned_store_ids = [sid];
    setEditing(null); setForm(initial); setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try { await api.delete(`/employees/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleAssign = (storeId) => setForm((f) => ({
    ...f,
    assigned_store_ids: f.assigned_store_ids?.includes(storeId)
      ? f.assigned_store_ids.filter((x) => x !== storeId)
      : [...(f.assigned_store_ids || []), storeId],
  }));

  const roleLabel = (r) => ({ super_admin: "Owner", admin: "Admin", manager: "Store Manager", chef: "Chef", cashier: "Cashier" }[r] || "Employee");
  const roleStyle = (r) => ({
    super_admin: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]",
    admin: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]",
    manager: "bg-purple-100 text-purple-700",
    chef: "bg-amber-100 text-amber-700",
    cashier: "bg-sky-100 text-sky-700",
  }[r] || "bg-neutral-100");

  return (
    <div className="p-8 lg:p-10">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">Team</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-employees-title">Employees</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {sid ? "Team members assigned to this store." : "Admin-only. Assign staff to one or more stores and toggle permissions."}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} className="rounded-xl bg-[hsl(var(--primary))]" data-testid="add-employee"><Plus className="w-4 h-4 mr-1"/>Add Employee</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Team Member" : "New Employee"}</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[75vh] overflow-y-auto pr-1">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="emp-name"/></div>
                {!editing && (<>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="emp-email"/></div>
                  <div><Label>Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) => setForm({...form, password: e.target.value})}
                        className="pr-10"
                        data-testid="emp-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-700"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        data-testid="emp-password-toggle"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                      </button>
                    </div>
                  </div>
                </>)}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Position</Label><Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})}/></div>
                  <div><Label>Hourly Rate</Label><Input type="number" step="0.5" value={form.hourly_rate} onChange={(e) => setForm({...form, hourly_rate: e.target.value})}/></div>
                </div>
                <div>
                  <Label>Role</Label>
                  <select
                    value={form.role}
                    onChange={(e) => onRoleChange(e.target.value)}
                    className="h-10 w-full rounded-lg border border-neutral-200 bg-white text-sm px-3"
                    data-testid="emp-role"
                  >
                    <option value="cashier">Cashier</option>
                    <option value="chef">Chef</option>
                    <option value="employee">Employee (general)</option>
                    <option value="manager">Store Manager</option>
                    <option value="admin">Admin</option>
                    {isSuper && <option value="super_admin">Super Admin (Owner)</option>}
                  </select>
                </div>

                {/* Multi-store assignments */}
                <div>
                  <Label>Assigned Stores <span className="text-xs text-neutral-400 font-normal">(pick one or more)</span></Label>
                  <div className="grid grid-cols-2 gap-2 mt-1 max-h-40 overflow-y-auto p-2 border border-neutral-200 rounded-lg bg-neutral-50">
                    {stores.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.assigned_store_ids?.includes(s.id)}
                          onChange={() => toggleAssign(s.id)}
                          data-testid={`emp-assign-${s.id}`}
                        />
                        <span>{s.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div><Label>Work Agreement (terms, weekly hours, etc.)</Label><Textarea rows={2} value={form.work_agreement} onChange={(e) => setForm({...form, work_agreement: e.target.value})}/></div>

                {form.role === "manager" && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/20">
                    <div>
                      <div className="font-medium text-sm">Grant view of employees</div>
                      <div className="text-xs text-neutral-500">Manager can see their store's team & clock records</div>
                    </div>
                    <Switch checked={form.can_view_employees} onCheckedChange={(v) => setForm({...form, can_view_employees: v})} data-testid="emp-can-view"/>
                  </div>
                )}

                {/* Permissions matrix */}
                <div className="border-t border-neutral-200 pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-[hsl(var(--primary))]"/>
                    <Label className="text-sm font-semibold m-0">Permissions</Label>
                  </div>
                  <p className="text-[11px] text-neutral-500 mb-2">Fine-tune what this staff member can do. Defaults come from their role.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {PERMISSION_KEYS.map((k) => (
                      <div key={k} className="flex items-center justify-between gap-2 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-1.5">
                        <span className="text-sm capitalize">{k}</span>
                        <select
                          value={form.permissions?.[k] || "none"}
                          onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, [k]: e.target.value } })}
                          className="h-8 rounded-md border border-neutral-200 bg-white text-xs px-2"
                          data-testid={`perm-${k}`}
                        >
                          {(VERB_BY_KEY[k] || ["none", "view", "manage"]).map((v) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                          <option value="manage">manage</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={save} className="w-full rounded-xl bg-[hsl(var(--primary))]" data-testid="emp-save">{editing ? "Update" : "Create"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Name</th>
              <th className="p-4">Email</th>
              <th className="p-4">Stores</th>
              <th className="p-4">Position</th>
              <th className="p-4">Rate</th>
              <th className="p-4">Role</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {list.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-neutral-500" data-testid="emp-empty">No employees.</td></tr>
            )}
            {list.map((e) => (
              <tr key={e.id} data-testid={`emp-row-${e.id}`}>
                <td className="p-4 font-medium"><Link to={`${sid ? `/admin/stores/${sid}` : "/admin"}/employees/${e.id}`} className="hover:text-[hsl(var(--primary))] underline-offset-2 hover:underline" data-testid={`emp-detail-${e.id}`}>{e.name}</Link></td>
                <td className="p-4 text-neutral-600">{e.email}</td>
                <td className="p-4 text-neutral-600 max-w-[240px] truncate">{storeNames(employeeStoreIds(e))}</td>
                <td className="p-4">{e.position || "—"}</td>
                <td className="p-4 font-mono">{e.hourly_rate ? `$${e.hourly_rate}` : "—"}</td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${roleStyle(e.role)}`} data-testid={`emp-role-badge-${e.id}`}>{roleLabel(e.role)}</span>
                  {e.role === "manager" && e.can_view_employees && <span className="ml-2 text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">team view</span>}
                </td>
                <td className="p-4 flex gap-2">
                  <button onClick={() => setViewing(e)} className="text-neutral-500 hover:text-[hsl(var(--primary))]"><Eye className="w-4 h-4"/></button>
                  {isAdmin && <button onClick={() => openEdit(e)} className="text-neutral-500 hover:text-[hsl(var(--primary))]"><Pencil className="w-4 h-4"/></button>}
                  {isAdmin && <button onClick={() => remove(e.id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-md">
          {viewing && (
            <>
              <DialogHeader><DialogTitle>{viewing.name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3 text-neutral-600">
                  <div><span className="text-xs text-neutral-500">Email</span><div>{viewing.email}</div></div>
                  <div><span className="text-xs text-neutral-500">Role</span><div className="capitalize">{roleLabel(viewing.role)}</div></div>
                  <div><span className="text-xs text-neutral-500">Position</span><div>{viewing.position || "—"}</div></div>
                  <div><span className="text-xs text-neutral-500">Hourly rate</span><div>${viewing.hourly_rate || 0}/hr</div></div>
                  <div className="col-span-2"><span className="text-xs text-neutral-500">Stores</span><div>{storeNames(employeeStoreIds(viewing))}</div></div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Permissions</div>
                  <div className="grid grid-cols-2 gap-1 text-xs bg-neutral-50 rounded-lg p-3">
                    {Object.entries(viewing.permissions || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span className="text-neutral-500">{v}</span></div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Work agreement</div>
                  <div className="text-neutral-800 whitespace-pre-wrap bg-neutral-50 rounded-lg p-3">{viewing.work_agreement || "—"}</div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
